import { STOCK_SLIP_TYPE } from '@/utils/enum'
import {
  getAutoStockSlipModalDirection,
  isStockSlipViewOnly,
} from '@/utils/stockSlipOrigin'

/** stockSlipType -> bill route when billId is present */
const BILL_TYPE_ROUTES = Object.freeze({
  [STOCK_SLIP_TYPE.EXPORT_STOCK_TO_SALE]: { name: 'update-bill', queryKey: 'id' },
  [STOCK_SLIP_TYPE.EXPORT_STOCK_TO_SALE_WHOLSE]: { name: 'update-wholesale-invoice', queryKey: 'id' },
  [STOCK_SLIP_TYPE.IMPORT_STOCK_FROM_BILL]: { name: 'update-refund-bill', queryKey: 'billNumber' },
  [STOCK_SLIP_TYPE.IMPORT_WARRANTY_FROM_BILL]: { name: 'update-bill', queryKey: 'id' },
  [STOCK_SLIP_TYPE.EXPORT_WARRANTY_FROM_BILL]: { name: 'update-bill', queryKey: 'id' },
  [STOCK_SLIP_TYPE.EXPORT_STOCK_BY_WARRANTY_BILL]: { name: 'update-bill', queryKey: 'id' },
  [STOCK_SLIP_TYPE.EXPORT_WARRANTY_STOCK_BY_DELETE_WARRANTY_BILL]: { name: 'update-bill', queryKey: 'id' },
  [STOCK_SLIP_TYPE.IMPORT_STOCK_BY_DELETE_WARRANTY_BILL]: { name: 'update-bill', queryKey: 'id' },
  [STOCK_SLIP_TYPE.IMPORT_STOCK_BY_DELETE_STORE_WARRANTY_BILL]: { name: 'update-bill', queryKey: 'id' },
  [STOCK_SLIP_TYPE.EXPORT_STOCK_BY_CREATE_STORE_WARRANTY_BILL]: { name: 'update-bill', queryKey: 'id' },
})

/** stockSlipType -> stock slip edit route (query.id = stockSlipId) */
const STOCK_SLIP_TYPE_TO_EDIT_ROUTE = Object.freeze({
  [STOCK_SLIP_TYPE.IMPORT_PROVIDER]: 'edit-inventory-receipt',
  [STOCK_SLIP_TYPE.EXPORT_PROVIDER]: 'edit-inventory-dispatch',
  [STOCK_SLIP_TYPE.TRANSFER]: 'edit-inventory-transfer',
  4: 'edit-request-transfer',
  [STOCK_SLIP_TYPE.REQUEST_TRANSFER]: 'edit-request-transfer',
  [STOCK_SLIP_TYPE.IMPORT_STOCK_FROM_ORDER]: 'edit-inventory-receipt',
  [STOCK_SLIP_TYPE.EXPORT_TO_SHIPPING]: 'edit-inventory-dispatch',
  [STOCK_SLIP_TYPE.IMPORT_FROM_TRADE_IN]: 'update-import-trade-in',
  [STOCK_SLIP_TYPE.IMPORT_WARRANTY]: 'edit-inventory-receipt',
  [STOCK_SLIP_TYPE.EXPORT_WARRANTY]: 'edit-inventory-dispatch',
  [STOCK_SLIP_TYPE.TRANSFER_WARRANTY_STOCK]: 'edit-inventory-transfer',
  [STOCK_SLIP_TYPE.REQUIRE_EXPORT_WARRANTY_STOCK]: 'edit-request-transfer',
  [STOCK_SLIP_TYPE.REQUIRE_IMPORT_WARRANTY_STOCK]: 'edit-request-transfer',
  [STOCK_SLIP_TYPE.EXPORT_WARRANTY_TO_PROVIDER]: 'edit-inventory-dispatch',
  [STOCK_SLIP_TYPE.UPDATE_EXPORT_WARRANTY_TO_PROVIDER]: 'edit-inventory-dispatch',
  [STOCK_SLIP_TYPE.DELETE_EXPORT_WARRANTY_TO_PROVIDER]: 'edit-inventory-dispatch',
  [STOCK_SLIP_TYPE.CREATE_WARRANTY_COMPLETION_SLIP]: 'edit-inventory-dispatch',
  [STOCK_SLIP_TYPE.DELETE_WARRANTY_COMPLETION_SLIP]: 'edit-inventory-dispatch',
  [STOCK_SLIP_TYPE.IMPORT_STOCK_BY_WARRANTY_COMPLETION]: 'edit-inventory-receipt',
  [STOCK_SLIP_TYPE.EXPORT_STOCK_BY_WARRANTY_COMPLETION]: 'edit-inventory-dispatch',
  [STOCK_SLIP_TYPE.EXPORT_STOCK_BY_INVENTORY_CHECK]: 'upsert-inventory-check',
  [STOCK_SLIP_TYPE.IMPORT_STOCK_BY_INVENTORY_CHECK]: 'upsert-inventory-check',
  [STOCK_SLIP_TYPE.EXPORT_LENDING]: 'edit-lending-stockslip',
  [STOCK_SLIP_TYPE.IMPORT_STOCK_LENDING]: 'edit-lending-stockslip',
  [STOCK_SLIP_TYPE.IMPORT_WARRANTY_FROM_PROVIDER]: 'edit-inventory-receipt',
  [STOCK_SLIP_TYPE.IMPORT_FROM_CHANGE_PRODUCT]: 'production-order',
  [STOCK_SLIP_TYPE.EXPORT_FROM_CHANGE_PRODUCT]: 'production-order',
  [STOCK_SLIP_TYPE.IMPORT_FROM_CHANGE_IMEI]: 'production-order',
  [STOCK_SLIP_TYPE.EXPORT_FROM_CHANGE_IMEI]: 'production-order',
  [STOCK_SLIP_TYPE.IMPORT_WARRANTY_OUTSOURCE]: 'edit-inventory-receipt',
  49: 'update-opening-stock-receipt',
  51: 'edit-defective-goods-transfer',
  52: 'edit-defective-goods-export',
  53: 'edit-inventory-transfer',
  [STOCK_SLIP_TYPE.IMPORT_HKD]: 'edit-inventory-receipt',
  [STOCK_SLIP_TYPE.EXPORT_HKD]: 'edit-inventory-dispatch',
})

function looksLikeStockSlipCode(code) {
  if (!code) return false
  return /[a-zA-Z]/.test(String(code)) || String(code).includes('.')
}

/**
 * Normalize IMEI history or product stock history row to a common shape.
 */
export function normalizeDocumentItem(item) {
  if (!item) return null

  const stockSlipCode = item.stockSlipCode ?? item.code ?? ''
  const stockSlipType = item.stockSlipType ?? item.type
  const stockSlipId = item.stockSlipId

  let redirectType = item.redirectType
  if (!redirectType) {
    if (item.note === 'REF:ORDER') {
      redirectType = 'order'
    } else if (/^Đơn #/.test(stockSlipCode)) {
      redirectType = 'order'
    } else if (
      stockSlipType === 1 &&
      stockSlipId != null &&
      !looksLikeStockSlipCode(stockSlipCode)
    ) {
      redirectType = 'order'
    }
  }

  let billId = item.billId
  if (
    billId == null &&
    BILL_TYPE_ROUTES[stockSlipType] &&
    stockSlipCode &&
    /^\d+$/.test(String(stockSlipCode))
  ) {
    billId = stockSlipCode
  }

  const orderLabel =
    stockSlipCode || (stockSlipId != null ? `Đơn #${stockSlipId}` : '')

  return {
    redirectType,
    billId,
    stockSlipType,
    stockSlipId,
    stockSlipCode: redirectType === 'order' ? orderLabel : stockSlipCode,
    note: item.note,
  }
}

function defaultSlipLabel(item) {
  return item.stockSlipCode || (item.stockSlipId != null ? String(item.stockSlipId) : '')
}

function buildStockSlipRoute(routeName, stockSlipId) {
  return {
    name: routeName,
    params: {},
    query: { id: stockSlipId },
  }
}

function buildBillRoute(billConfig, billId) {
  return {
    name: billConfig.name,
    params: {},
    query: { [billConfig.queryKey]: billId },
  }
}

function resolveOrderDocument(item) {
  const label =
    item.stockSlipCode || (item.stockSlipId != null ? `Đơn #${item.stockSlipId}` : '')
  if (!item.stockSlipId) {
    return { label, clickable: false, route: null }
  }
  return {
    label,
    clickable: true,
    route: {
      name: 'edit-order',
      params: { id: item.stockSlipId },
      query: {},
    },
  }
}

function resolveWarrantyDocument(item) {
  const label = item.stockSlipCode || (item.stockSlipId != null ? `BH #${item.stockSlipId}` : '')
  if (!item.stockSlipId) {
    return { label, clickable: false, route: null }
  }
  return {
    label,
    clickable: true,
    route: {
      name: 'warranty-dispatch-info',
      params: {},
      query: { id: item.stockSlipId },
    },
  }
}

function resolveBillDocument(item) {
  const billConfig = BILL_TYPE_ROUTES[item.stockSlipType]
  if (!billConfig || item.billId == null) {
    return null
  }
  return {
    label: `HĐ #${item.billId}`,
    clickable: true,
    route: buildBillRoute(billConfig, item.billId),
  }
}

function resolveStockSlipDocument(item) {
  const label = defaultSlipLabel(item)
  if (item.stockSlipId == null) {
    return { label, clickable: false, route: null, viewMode: 'route', modalPayload: null }
  }

  if (isStockSlipViewOnly(item.stockSlipType)) {
    return {
      label,
      clickable: true,
      viewMode: 'modal',
      modalPayload: {
        slipId: item.stockSlipId,
        slipType: item.stockSlipType,
        direction: getAutoStockSlipModalDirection(item.stockSlipType),
      },
      route: null,
    }
  }

  const routeName = STOCK_SLIP_TYPE_TO_EDIT_ROUTE[item.stockSlipType]
  if (!routeName) {
    return { label, clickable: false, route: null, viewMode: 'route', modalPayload: null }
  }

  return {
    label,
    clickable: true,
    viewMode: 'route',
    route: buildStockSlipRoute(routeName, item.stockSlipId),
    modalPayload: null,
  }
}

/**
 * Resolve display label and navigation target for an IMEI history row.
 * @returns {{ label: string, clickable: boolean, route: { name: string, params: object, query: object } | null }}
 */
export function resolveImeiHistoryDocument(item) {
  const normalized = normalizeDocumentItem(item)
  if (!normalized) {
    return { label: '', clickable: false, route: null }
  }

  const redirectType = normalized.redirectType

  if (redirectType === 'order') {
    return resolveOrderDocument(normalized)
  }

  if (redirectType === 'warranty') {
    return resolveWarrantyDocument(normalized)
  }

  const billDoc = resolveBillDocument(normalized)
  if (billDoc) {
    return billDoc
  }

  return resolveStockSlipDocument(normalized)
}

/** Alias for product stock history rows (same resolver). */
export const resolveStockHistoryDocument = resolveImeiHistoryDocument

/** Open resolved document in a new tab, or invoke modal for auto stock slips. */
export function openImeiHistoryDocument(router, item, options = {}) {
  const doc = resolveImeiHistoryDocument(item)
  if (!doc.clickable) {
    return
  }

  if (doc.viewMode === 'modal' && doc.modalPayload) {
    if (typeof options.onOpenModal === 'function') {
      options.onOpenModal(doc.modalPayload)
      return
    }
    const { slipId, direction } = doc.modalPayload
    const routeName =
      direction === 'import' ? 'view-inventory-receipt-auto' : 'view-inventory-dispatch-auto'
    const { href } = router.resolve({ name: routeName, query: { id: slipId, viewOnly: '1' } })
    window.open(href, '_blank')
    return
  }

  if (!doc.route) {
    return
  }
  const { href } = router.resolve(doc.route)
  window.open(href, '_blank')
}

export const openStockHistoryDocument = openImeiHistoryDocument
